/**
 * Auth API — typed wrappers around Amplify auth operations.
 *
 * Re-exports from the legacy authApi.js with proper types.
 * Also wraps group/user API calls needed during signup confirmation.
 */
import {
  signIn as legacySignIn,
  signUp as legacySignUp,
  confirmSignUp as legacyConfirmSignUp,
  signOut as legacySignOut,
  completeNewPassword as legacyCompleteNewPassword,
  forgotPassword as legacyForgotPassword,
  forgotPasswordSubmit as legacyForgotPasswordSubmit,
  resendVerificationCode as legacyResendVerificationCode,
  getCurrentSession as legacyGetCurrentSession,
} from '../apiServices/authApi';
import { addGroupWithHeader } from '../apiServices/groupApi';
import { addInvitedUser } from '../apiServices/userApi';
import { getInvitedUserEmail } from '../apiServices/userApi';

// ── Types ───────────────────────────────────────────────────────────

interface SignInResult {
  challengeName?: 'NEW_PASSWORD_REQUIRED';
  [key: string]: unknown;
}

interface SignUpResult {
  userSub: string;
}

interface SessionResult {
  idToken: { jwtToken: string };
  accessToken: { jwtToken: string };
}

interface InvitedUserInfo {
  email: { S: string };
  groupTitle: { S: string };
  id: { S: string };
}

// ── Auth functions ──────────────────────────────────────────────────

export async function signIn(email: string, password: string): Promise<SignInResult> {
  return legacySignIn(email.toLowerCase(), password);
}

export async function signUp(params: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}): Promise<SignUpResult> {
  return legacySignUp({
    username: params.email.toLowerCase(),
    password: params.password,
    attributes: {
      given_name: params.firstName,
      family_name: params.lastName,
    },
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  await legacyConfirmSignUp(email, code);
}

export async function completeNewPassword(user: unknown, newPassword: string): Promise<void> {
  await legacyCompleteNewPassword(user, newPassword);
}

export async function forgotPassword(email: string): Promise<void> {
  await legacyForgotPassword(email);
}

export async function forgotPasswordSubmit(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await legacyForgotPasswordSubmit(email, code, newPassword);
}

export async function resendVerificationCode(email: string): Promise<void> {
  await legacyResendVerificationCode(email);
}

export async function getCurrentSession(): Promise<SessionResult> {
  return legacyGetCurrentSession();
}

export async function signOut(): Promise<void> {
  await legacySignOut();
}

// ── Post-signup helpers ─────────────────────────────────────────────

export async function createWorkspaceAfterSignup(
  session: SessionResult,
  companyName: string,
  userSub: string,
  firstName: string,
  lastName: string,
  email: string,
): Promise<void> {
  const header = { Authorization: session.idToken.jwtToken };
  await addGroupWithHeader(companyName, userSub, header, firstName, lastName, email);
}

export async function acceptInvitationAfterSignup(
  session: SessionResult,
  invitationId: string,
  firstName: string,
  lastName: string,
): Promise<void> {
  const header = { Authorization: session.idToken.jwtToken };
  await addInvitedUser(invitationId, firstName, lastName, header);
}

export async function fetchInvitedUserEmail(invitationId: string): Promise<InvitedUserInfo> {
  const response = await getInvitedUserEmail(invitationId);
  return (response as { response: { Item: InvitedUserInfo } }).response.Item;
}
